import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import i18n from '@/i18n';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyUserLanguage = useCallback((lang) => {
    const code = (lang || '').toString().toLowerCase().slice(0, 5);
    const allowed = ['fr', 'en', 'de', 'es', 'it'];
    if (code && allowed.includes(code) && i18n.language !== code) {
      i18n.changeLanguage(code);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('pp_access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      applyUserLanguage(data.language);
    } catch {
      localStorage.removeItem('pp_access_token');
      localStorage.removeItem('pp_refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [applyUserLanguage]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.pending_approval) {
      // Subcontractor account awaiting admin validation — do NOT authenticate.
      return data;
    }
    localStorage.setItem('pp_access_token', data.access_token);
    localStorage.setItem('pp_refresh_token', data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    // Fetch full profile to pick up stored language preference
    try {
      const me = await api.get('/auth/me');
      setUser(me.data);
      applyUserLanguage(me.data.language);
    } catch { /* ignore */ }
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('pp_access_token', data.access_token);
    localStorage.setItem('pp_refresh_token', data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('pp_access_token');
    localStorage.removeItem('pp_refresh_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
